import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import FormInput from "components/FormInput/index";
import FormSelect from "components/UnifiedSelect";
import TableSearch from "components/Table/TableSearch";
import FilterPills from "components/Table/FilterPills";
import {
  EMPTY_FILTERS,
  toOption,
} from "../utilityUtils";
import { CommodityIcon } from "../CommodityIcons";

const PlainMultiValue = ({ data, index, getValue }) => {
  const selectedValues = getValue();
  const isLast = index === selectedValues.length - 1;

  return (
    <span className="utility-select-value">
      {data.label}{isLast ? "" : ", "}
    </span>
  );
};

const FilterModal = forwardRef(({
  onHide,
  filters,
  onApply,
  utilityOptions = [],
  countries = [],
  states = [],
  onCountryChange,
}, ref) => {
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState({
    ...EMPTY_FILTERS,
    ...filters,
  });

  const { control, getValues, reset } = useForm({
    defaultValues: {
      Active: Boolean(filters.Active),
      Inactive: Boolean(filters.Inactive),
      Electric: Boolean(filters.Electric),
      NatGas: Boolean(filters.NatGas),
    },
  });

  const previousFilters = useRef(filters);

  const getCurrentDraft = () => {
    const values = getValues();

    return {
      ...draft,
      SearchValue: search.trim() || filters.SearchValue || "",
      Active: Boolean(values.Active),
      Inactive: Boolean(values.Inactive),
      Electric: Boolean(values.Electric),
      NatGas: Boolean(values.NatGas),
    };
  };

  const applyFilters = () => {
    onApply(getCurrentDraft());
    setSearch("");
    onHide();
  };

  useImperativeHandle(ref, () => ({
    handleApply: applyFilters,
    handleReset: () => {
      const resetFilters = { ...EMPTY_FILTERS };

      reset({
        Active: resetFilters.Active,
        Inactive: resetFilters.Inactive,
        Electric: resetFilters.Electric,
        NatGas: resetFilters.NatGas,
      });

      setSearch("");
      setDraft(resetFilters);
      onCountryChange?.("");
      onApply(resetFilters);
    },
  }));

  const [loadingStates, setLoadingStates] = useState(false);

  useEffect(() => {
    const changes = Object.fromEntries(Object.entries(filters).filter(
      ([key, value]) => JSON.stringify(value) !== JSON.stringify(previousFilters.current[key])
    ));
    previousFilters.current = filters;
    // Removing one pill must not discard pending changes in the other fields.
    setDraft(current => ({ ...current, ...changes }));
    const checkboxChanges = Object.fromEntries(Object.entries(changes).filter(
      ([key]) => ["Active", "Inactive", "Electric", "NatGas"].includes(key)
    ));
    if (Object.keys(checkboxChanges).length) reset({ ...getValues(), ...checkboxChanges });
  }, [filters, reset, getValues]);

  const countryOptions = useMemo(
    () => countries.map((country) => toOption(country)),
    [countries]
  );

  const stateOptions = useMemo(
    () =>
      states.map((state) =>
        toOption(state, [
          "displayName",
          "DisplayName",
          "name",
          "Name",
          "stateName",
          "StateName",
        ])
      ),
    [states]
  );

  const parentOptions = useMemo(
    () => utilityOptions.map((utility) => toOption(utility)),
    [utilityOptions]
  );

  const selectedCountry =
    countryOptions.find(
      (option) => option.value === draft.CountryIdentifier
    ) || null;

  const selectedStates = stateOptions.filter((option) =>
    draft.StateExIDs?.includes(option.value)
  );

  const selectedParents = parentOptions.filter((option) =>
    draft.ParentUtilityExIDs?.includes(option.value)
  );

  const stateLabelCache = useRef(new Map());
  useEffect(() => {
    stateOptions.forEach(option => stateLabelCache.current.set(option.value, option.label));
  }, [stateOptions]);

  const removeFilter = (changes) => {
    onApply({ ...filters, ...changes });
  };
  const optionLabels = (options, values, cache) => (values || [])
    .map(value => options.find(option => option.value === value)?.label || cache?.get(value) || value).join(", ");

  const handleCountryChange = async (selectedOption) => {
    const countryIdentifier = selectedOption?.value || "";

    setDraft((current) => ({
      ...current,
      CountryIdentifier: countryIdentifier,
      StateExIDs: [],
    }));

    setLoadingStates(true);

    try {
      if (onCountryChange) {
        await onCountryChange(countryIdentifier);
      }
    } finally {
      setLoadingStates(false);
    }
  };

  return (
    <div className="utility-filter">
      <div className="utility-filter-row">
        <div className="utility-filter-field">
          <label className="utility-filter-label">
            Country
          </label>
          <FormSelect
            options={countryOptions}
            value={selectedCountry}
            onChange={handleCountryChange}
            isClearable
            getOptionValue={(option) => String(option.value)}
            getOptionLabel={(option) => option.label}
            placeholder="Select country"
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <div className="utility-filter-field">
          <label className="utility-filter-label">
            State
          </label>
          <FormSelect
            isMulti
            options={stateOptions}
            value={selectedStates}
            isLoading={loadingStates}
            components={{ MultiValue: PlainMultiValue }}
            isClearable
            getOptionValue={(option) => String(option.value)}
            getOptionLabel={(option) => option.label}
            onChange={(selectedValues) =>
              setDraft((current) => ({
                ...current,
                StateExIDs: (selectedValues || []).map((option) => option.value),
              }))
            }
            placeholder="Select states"
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <div className="utility-filter-field">
          <label className="utility-filter-label">
            Parent Utility
          </label>
          <FormSelect
            isMulti
            options={parentOptions}
            value={selectedParents}
            components={{ MultiValue: PlainMultiValue }}
            isClearable
            getOptionValue={(option) => String(option.value)}
            getOptionLabel={(option) => option.label}
            onChange={(selectedValues) =>
              setDraft((current) => ({
                ...current,
                ParentUtilityExIDs: (selectedValues || []).map((option) => option.value),
              }))
            }
            placeholder="Select parent utilities"
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <div className="utility-filter-field">
          <label className="utility-filter-label">Name or Long Name</label>
          <TableSearch
            value={search}
            onChange={setSearch}
            onSearch={applyFilters}
            label="Name or Long Name"
            placeholder="Search by name or long name"
            showAction={false}
            showApplied={false}
          />
        </div>

        <div className="utility-filter-field utility-filter-field--compact">
          <label className="utility-filter-label">
            Status
          </label>
          <div className="utility-filter-checks">
            <FormInput
              type="checkbox"
              name="Active"
              control={control}
              label="Active"
              containerClass="mb-0"
              inputClassName="utility-filter-check"
            />

            <FormInput
              type="checkbox"
              name="Inactive"
              control={control}
              label="Inactive"
              containerClass="mb-0"
              inputClassName="utility-filter-check"
            />
          </div>
        </div>

        <div className="utility-filter-field utility-filter-field--compact">
          <span className="utility-sr-only">
            Commodity
          </span>
          <div className="utility-filter-checks">
            <FormInput
              type="checkbox"
              name="Electric"
              control={control}
              label={(
                <span className="utility-filter-icon-label">
                  <CommodityIcon type="electric" />
                  <span className="utility-sr-only">Electric</span>
                </span>
              )}
              containerClass="mb-0"
              inputClassName="utility-filter-check"
            />

            <FormInput
              type="checkbox"
              name="NatGas"
              control={control}
              label={(
                <span className="utility-filter-icon-label">
                  <CommodityIcon type="gas" />
                  <span className="utility-sr-only">Natural Gas</span>
                </span>
              )}
              containerClass="mb-0"
              inputClassName="utility-filter-check"
            />
          </div>
        </div>
      </div>
      <FilterPills filters={[
        { label: "Search", value: filters.SearchValue, onRemove: () => removeFilter({ SearchValue: "" }) },
        { label: "Country", value: optionLabels(countryOptions, filters.CountryIdentifier ? [filters.CountryIdentifier] : []),
          onRemove: () => { removeFilter({ CountryIdentifier: "", StateExIDs: [] }); onCountryChange?.(""); } },
        { label: "State", value: optionLabels(stateOptions, filters.StateExIDs, stateLabelCache.current), onRemove: () => removeFilter({ StateExIDs: [] }) },
        { label: "Parent Utility", value: optionLabels(parentOptions, filters.ParentUtilityExIDs), onRemove: () => removeFilter({ ParentUtilityExIDs: [] }) },
        { label: "Status", value: [filters.Active && "Active", filters.Inactive && "Inactive"].filter(Boolean).join(", "),
          onRemove: () => removeFilter({ Active: false, Inactive: false }) },
        { label: "Commodity", value: [filters.Electric && "Electric", filters.NatGas && "Natural Gas"].filter(Boolean).join(", "),
          onRemove: () => removeFilter({ Electric: false, NatGas: false }) },
      ]} />
    </div>
  );
});

export default FilterModal;